import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslator } from '@picsart/rc/services/localization';
import Checkbox from '@picsart/checkbox/Checkbox';
import View from '@picsart/view/View';
import Text from '@picsart/text/Text';
import { Typography, Spacings } from '@picsart/cascade';
import { Texts } from '@picsart/cascade/colors/texts';
import { FontWeights } from '@picsart/cascade/fonts';
import IconDelete from '@picsart/icons/IconDelete';
import IconRefresh from '@picsart/icons/IconRefresh';
import { ControlSizesSM, InlineButton } from '@picsart/inlineButton';

import { DataActionTypes } from 'pages/Batch/sections/Context';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import BatchDataService from 'pages/Batch/sections/Context/BatchDataService';
import errorLogger from 'services/errorLogger';
import analytics from 'pages/Batch/utils/analytics';
import batchSlice from 'store/batch';

import useStyles from './styles';
import testIds from './testIds';

export interface IBulkToolbar {
  onDelete: () => void;
  onSelectAll: () => void;
  isAllSelected: boolean;
}

const Toolbar = ({
  onDelete,
  onSelectAll,
  isAllSelected,
}: IBulkToolbar) => {
  const classes = useStyles();
  const translate = useTranslator();
  const dispatch = useDispatch();
  const {
    dispatchData,
    selectedData,
    selectedKeys,
    data,
    isWeb,
  } = useBatchData();

  // TODO: shouldResetAppliedTools, find out way to fix bug
  // imagine we have 2 images, for one we applied premium tool for another free tool
  // select image with premium tool and reset it, now we have only one applied tool which is free
  // since not all items were reverted, we are not resetting appliedPremiumTools but we should
  const shouldResetAppliedTools = useMemo(() => {
    const processedData = data.filter(x => x.isProcessed);
    const processedSelectedData = selectedData.filter(x => x.isProcessed);
    return processedSelectedData.length === processedData.length;
  }, [data, selectedData]);

  const isShowReset = useMemo(() => !!selectedKeys.length && selectedData.find(item => item.isProcessed),
    [selectedData, selectedKeys.length]);

  const handleReset = useCallback(() => {
    analytics.clickEvent('reset');
    dispatchData({
      type: DataActionTypes.RESET_DATA,
      keys: selectedKeys,
    });
    if (shouldResetAppliedTools) {
      dispatch(batchSlice.actions.resetAppliedPremiumTools());
    }

    isWeb && BatchDataService.resetData(selectedKeys);
    errorLogger.leaveBreadcrumb('toolbar_click', { button: 'reset' });
  }, [dispatchData, selectedKeys, shouldResetAppliedTools, dispatch, isWeb]);

  return (
    <View
      flexDirection="row"
      justifyContent="space-between"
      width="100%"
      position="absolute"
      insetBlockStart={Spacings.s0}
      insetInlineStart={Spacings.s0}
      height="40px"
    >
      <View
        onClick={onSelectAll}
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        className={classes.cursorPointer}
      >
        <Checkbox className={classes.selectCheckbox} isChecked={isAllSelected} onChange={(e: MouseEvent) => e.preventDefault} />
        <View marginInlineStart={Spacings.s8} dataTestId={testIds.selectAllBtn}>
          <Text weight={FontWeights.Bold} size={Typography.t5} color={Texts.tint1}>{translate('Select all')}</Text>
        </View>
      </View>

      <View
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
      >
        {!!selectedKeys.length && (
          <InlineButton
            data-testid={testIds.deleteBtn}
            onClick={onDelete}
            size={ControlSizesSM}
            startIcon={IconDelete}
            isWithoutBorder
            isWithoutBackground
          >
            <Text weight={FontWeights.Medium} size={Typography.t4} color={Texts.tint1}>{translate('Delete')}</Text>
          </InlineButton>
        )}

        {isShowReset && (
          <InlineButton
            data-testid={testIds.resetBtn}
            onClick={handleReset}
            size={ControlSizesSM}
            startIcon={IconRefresh}
            isWithoutBorder
            isWithoutBackground
          >
            <Text weight={FontWeights.Medium} size={Typography.t4} color={Texts.tint1}>{translate('Reset')}</Text>
          </InlineButton>
        )}
      </View>
    </View>
  );
};

export default memo(Toolbar);
